// Double-entry arithmetic for the Money page.
//
// Nothing here is stored: an account has no balance field, it has postings. Every
// figure on the page is derived by summing signed minor units, which is why the
// books always balance and why no rounding ever creeps in — the contract keeps all
// amounts as integers (pence), and only the formatter divides.

import type { AccountInput, AssetInput, TransactionInput } from "@/types";
import type { EntityRecord } from "@/sync/entityCollection";

export type AccountRecord = EntityRecord<AccountInput>;
export type TransactionRecord = EntityRecord<TransactionInput>;
export type AssetRecord = EntityRecord<AssetInput>;

type AccountType = AccountInput["accountType"];

/**
 * The actual-only slice of a ledger. Every balance, net-worth, and income/spend
 * figure is derived from this — a `planned` transaction is intent and must never
 * reach a real total, or a plan would silently inflate net worth. A forecast opts
 * back in with {@link planned}.
 */
export function posted(transactions: TransactionRecord[]): TransactionRecord[] {
  return transactions.filter((t) => t.fields.status === "actual");
}

/** The planned-only slice — the future, for forecasts. Never summed into actuals. */
export function planned(transactions: TransactionRecord[]): TransactionRecord[] {
  return transactions.filter((t) => t.fields.status === "planned");
}

/** Sum every posting per account. The sum across all accounts is always zero. */
export function accountBalances(transactions: TransactionRecord[]): Map<string, number> {
  const balances = new Map<string, number>();
  for (const transaction of transactions) {
    for (const posting of transaction.fields.postings) {
      balances.set(posting.accountId, (balances.get(posting.accountId) ?? 0) + posting.amount);
    }
  }
  return balances;
}

/**
 * The same balances, restated in the base commodity.
 *
 * TODO(Epic 6): value each posting by its commodity + optional price (latest known
 * price for holdings). Until commodity-aware valuation lands, this is the identity
 * of {@link accountBalances} — correct only while every account shares the base
 * commodity.
 */
export function accountBalancesInBase(transactions: TransactionRecord[]): Map<string, number> {
  return accountBalances(transactions);
}

/**
 * A balance as a human reads it. Assets and expenses grow with debits (positive
 * postings); liabilities, income, and equity grow with credits (negative), so their
 * natural balance is negative in raw terms and is flipped for display.
 */
export function naturalBalance(type: AccountType, raw: number): number {
  return type === "asset" || type === "expense" ? raw : -raw;
}

/** An account and its subtree: its children, and the total those children roll up to. */
export type AccountTreeNode = {
  account: AccountRecord;
  children: AccountTreeNode[];
  /** Own natural balance plus every descendant's — what a collapsed parent still shows. */
  total: number;
};

/**
 * Arrange one account *class* into its parentId forest, with each node carrying the
 * rolled-up subtree balance. Callers pass a single type's accounts, so every node shares
 * one `accountType` and one natural-balance sign, and the totals add up cleanly.
 *
 * A parentId that points outside this set (a different class, or nothing) makes the account
 * a root here — each class stays a self-contained tree. A parentId cycle can't loop the
 * recursion: a node is expanded at most once.
 */
export function buildAccountTree(
  accounts: AccountRecord[],
  balances: Map<string, number>,
): AccountTreeNode[] {
  const inSet = new Set(accounts.map((a) => a.id));
  const childrenOf = new Map<string, AccountRecord[]>();
  const roots: AccountRecord[] = [];
  for (const account of accounts) {
    const parentId = account.fields.parentId;
    if (parentId && inSet.has(parentId)) {
      (childrenOf.get(parentId) ?? childrenOf.set(parentId, []).get(parentId)!).push(account);
    } else {
      roots.push(account);
    }
  }

  const visited = new Set<string>();
  const build = (account: AccountRecord): AccountTreeNode => {
    visited.add(account.id);
    const kids = (childrenOf.get(account.id) ?? [])
      .filter((child) => !visited.has(child.id))
      .sort((a, b) => a.fields.name.localeCompare(b.fields.name));
    const children = kids.map(build);
    const own = naturalBalance(account.fields.accountType, balances.get(account.id) ?? 0);
    const total = children.reduce((sum, child) => sum + child.total, own);
    return { account, children, total };
  };

  return roots
    .sort((a, b) => a.fields.name.localeCompare(b.fields.name))
    .map(build);
}

/** What the accounts are worth: assets less liabilities. */
export function netWorth(
  accounts: AccountRecord[],
  balances: Map<string, number>,
): number {
  return accounts.reduce((total, account) => {
    const raw = balances.get(account.id) ?? 0;
    if (account.fields.accountType === "asset") return total + raw;
    if (account.fields.accountType === "liability") return total - raw;
    return total;
  }, 0);
}

/** An Asset whose category marks it long-term; anything else is short-term. */
const FIXED_CATEGORY = "fixed";

/**
 * The balance sheet in the five-box layout of *Key Management Ratios*: two boxes of
 * assets (what the money was spent on) against three of liabilities and funds (where
 * it came from). Both columns total to the same number — total assets — which is the
 * whole point of the layout: five figures describe the entire structure.
 *
 * All figures are in the base currency, so the columns are commensurable.
 */
export type BalanceSheet = {
  /** Fixed assets — long-term: property, equipment, long investments. */
  fixedAssets: number;
  /** Current assets — short-term: cash, liquid securities, receivables. */
  currentAssets: number;
  /** Owners' funds — the owner's own claim. The contract calls this `equity`. */
  ownersFunds: number;
  /** Long-term loans — repayable beyond a year. */
  longTermLoans: number;
  /** Current liabilities — payable within a year. */
  currentLiabilities: number;
};

/** The four terms the book derives from the five boxes (chapter 3). */
export type BalanceSheetTerms = {
  /** TA = FA + CA = OF + LTL + CL. */
  totalAssets: number;
  /** CE = TA − CL = OF + LTL — the long-term funds. */
  capitalEmployed: number;
  /** NW = TA − CL − LTL = OF — what is left for the owner. */
  netWorth: number;
  /** WC = CA − CL — day-to-day operating liquidity. */
  workingCapital: number;
};

/**
 * Sort every account into its box. An asset account is *fixed* when an Asset record
 * marks it so; everything else — cash, bucket accounts, liquid securities — is
 * current, per the book's own rule that cash equivalents belong in current assets.
 *
 * Income and expense accounts are flows, not positions, and appear in no box: their
 * effect is already in the balances of the accounts they posted against.
 */
export function balanceSheet(
  accounts: AccountRecord[],
  assets: AssetRecord[],
  baseBalances: Map<string, number>,
): BalanceSheet {
  const fixedAccountIds = new Set(
    assets
      .filter((asset) => asset.fields.category === FIXED_CATEGORY)
      .map((asset) => asset.fields.accountId),
  );

  const sheet: BalanceSheet = {
    fixedAssets: 0,
    currentAssets: 0,
    ownersFunds: 0,
    longTermLoans: 0,
    currentLiabilities: 0,
  };

  for (const account of accounts) {
    const { accountType } = account.fields;
    const balance = naturalBalance(accountType, baseBalances.get(account.id) ?? 0);

    if (accountType === "asset") {
      if (fixedAccountIds.has(account.id)) sheet.fixedAssets += balance;
      else sheet.currentAssets += balance;
    } else if (accountType === "equity") {
      sheet.ownersFunds += balance;
    } else if (accountType === "liability") {
      // Every liability lands in CL: the contract gives an account no maturity, so
      // nothing here can tell a mortgage from a credit-card balance. LTL stays 0
      // until an account can say when it falls due.
      sheet.currentLiabilities += balance;
    }
  }
  return sheet;
}

export function balanceSheetTerms(sheet: BalanceSheet): BalanceSheetTerms {
  const totalAssets = sheet.fixedAssets + sheet.currentAssets;
  return {
    totalAssets,
    capitalEmployed: totalAssets - sheet.currentLiabilities,
    netWorth: totalAssets - sheet.currentLiabilities - sheet.longTermLoans,
    workingCapital: sheet.currentAssets - sheet.currentLiabilities,
  };
}

/** When a transaction sits on the timeline: when it happened, else when it is planned. */
export function transactionAt(transaction: TransactionRecord): string {
  return transaction.fields.actualAt ?? transaction.fields.plannedAt ?? "";
}

/** Every transaction that occurred in the given period key, e.g. "2026-07". */
export function inMonth(transactions: TransactionRecord[], month: string): TransactionRecord[] {
  return transactions.filter((t) => monthKey(transactionAt(t)) === month);
}

/** The period key an ISO timestamp falls in, e.g. "2026-07". */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

/** The period key `delta` months from the given one, e.g. ("2026-12", 1) → "2027-01". */
export function shiftMonth(month: string, delta: number): string {
  const [year, index] = splitMonth(month);
  // Date normalises out-of-range months across the year boundary for us.
  const shifted = new Date(Date.UTC(year, index - 1 + delta, 1));
  return monthKey(shifted.toISOString());
}

/** A period key as a person reads it, e.g. "2026-07" → "July 2026". */
export function formatMonth(month: string): string {
  const [year, index] = splitMonth(month);
  return new Date(Date.UTC(year, index - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function splitMonth(month: string): [number, number] {
  const [year, index] = month.split("-").map(Number);
  return [year ?? 0, index ?? 1];
}

/** Total earned in a period, across every income account. */
export function totalIncome(
  transactions: TransactionRecord[],
  accounts: AccountRecord[],
): number {
  return totalForType(transactions, accounts, "income");
}

/** Total spent in a period, across every expense account. */
export function totalSpend(
  transactions: TransactionRecord[],
  accounts: AccountRecord[],
): number {
  return totalForType(transactions, accounts, "expense");
}

function totalForType(
  transactions: TransactionRecord[],
  accounts: AccountRecord[],
  type: AccountType,
): number {
  const ids = idsOfType(accounts, type);
  const raw = transactions.reduce((total, t) => total + sumPostings(t, ids), 0);
  return naturalBalance(type, raw);
}

function idsOfType(accounts: AccountRecord[], type: AccountType): Set<string> {
  return new Set(
    accounts.filter((a) => a.fields.accountType === type).map((account) => account.id),
  );
}

function sumPostings(transaction: TransactionRecord, accountIds: Set<string>): number {
  return transaction.fields.postings
    .filter((posting) => accountIds.has(posting.accountId))
    .reduce((total, posting) => total + posting.amount, 0);
}

/**
 * Minor units → a display string, e.g. 550000 @ precision 2 → "£5,500.00". The
 * contract stores every amount as integer minor units of some commodity; the
 * divisor is that commodity's `precision` (2 for USD, 8 for BTC). ISO currencies
 * render through Intl; a non-currency commodity (AMZN, BTC) — which Intl rejects —
 * falls back to a fixed-decimal amount followed by its symbol.
 */
export function formatMoney(minorUnits: number, commoditySymbol: string, precision = 2): string {
  // `|| 0` collapses negative zero, which the sign flip in naturalBalance produces
  // for an empty period and which Intl would otherwise render as "-£0.00".
  const units = (minorUnits || 0) / 10 ** precision;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: commoditySymbol,
    }).format(units);
  } catch {
    // Not an ISO 4217 currency (a security, crypto, or property commodity).
    return `${units.toFixed(precision)} ${commoditySymbol}`;
  }
}
